import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { formatDisplayText } from '../../utils/text-case.utils';
import { ButtonComponent } from '../button/button.component';

type ToggleOption = {
  label: string;
  route?: string;
  key?: string;
  value?: string;
  default?: boolean;
  buttonWidth?: string;
  isQueryParam?: boolean;
  [key: string]: any;
};

@Component({
  selector: 'app-toggle-button',
  standalone: true,
  imports: [CommonModule, ButtonComponent],
  templateUrl: './toggle-button.component.html',
  styleUrl: './toggle-button.component.scss',
})
export class ToggleButtonComponent implements OnInit, OnDestroy {
  @Input() data!: any;

  options: ToggleOption[] = [];
  displayOptions: ToggleOption[] = [];
  topOptions: ToggleOption[] = [];
  bottomOptions: ToggleOption[] = [];
  isSpecialSplitLayout = false;
  activeRoute = '';
  private sub = new Subscription();

  constructor(private router: Router) { }

  ngOnInit(): void {
    this.options = this.data?.options || [];
    this.configureLayout(this.options);

    // JSON filter toggles deliberately reuse ButtonComponent. That component
    // owns the established toggleGroup state, filter events and styling used
    // by screens such as Column Selection / Saved Selection.
    if (this.isFilterToggle) {
      return;
    }

    this.activeRoute = this.router.url.split('?')[0];

    // keep activeRoute updated on navigation
    this.sub.add(
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe(() => (this.activeRoute = this.router.url.split('?')[0]))
    );

    // if current route is NOT one of the toggle routes, go to default
    const isOnAnyToggleRoute = this.options.some(o => o.route === this.activeRoute);
    const defaultOption = this.options.find(o => o.default);

    if (!isOnAnyToggleRoute && defaultOption) {
      this.router.navigateByUrl(defaultOption.route!);
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  isActive(option: ToggleOption): boolean {
    return this.activeRoute === option.route;
  }

  /**
   * Format the toggle label per UI Standards (Point 3).
   * Preserves special sentinels like "$" and "$+%" (used in split layout).
   * Override via data.textCase.
   */
  formatLabel(label: unknown): string {
    const raw = String(label ?? '').trim();
    // Preserve special symbols used by the split layout
    if (raw === '$' || raw === '$+%') return raw;
    const mode = (this.data?.textCase || 'capital') as any;
    return formatDisplayText(raw, mode);
  }

  onToggle(option: ToggleOption): void {
    if (this.isActive(option)) return;
    if (option.route) {
      this.router.navigateByUrl(option.route);
    }
  }

  /** Route options keep the original behavior; keyed options use filter state. */
  get isFilterToggle(): boolean {
    return this.options.some((option) => !!this.optionKey(option)) &&
      !this.options.some((option) => !!option?.route);
  }

  get filterToggleColumns(): string {
    return `repeat(${this.filterOptions.length}, minmax(0, 1fr))`;
  }

  get filterOptions(): ToggleOption[] {
    const requested = Number(this.data?.separatorConfig?.textboxCount);
    const count = Number.isFinite(requested)
      ? Math.min(3, Math.max(1, Math.trunc(requested)))
      : Math.min(3, this.options.length);
    return this.options.slice(0, count);
  }

  filterOptionData(option: ToggleOption): any {
    const key = this.optionKey(option);
    return {
      ...option,
      key,
      label: option.label,
      buttonWidth: option.buttonWidth ?? '100%',
      toggleGroup: this.data?.key || this.data?.toggleGroup,
      toggleGroupDefault: option.default === true || key === this.data?.defaultValue,
      isQueryParam: option.isQueryParam ?? this.data?.isQueryParam,
    };
  }

  private optionKey(option: ToggleOption): string {
    return String(option?.key ?? option?.value ?? '').trim();
  }

  private configureLayout(options: ToggleOption[]): void {
    const indexedOptions = options.map((option, index) => ({ option, index }));
    const bottomPriorityOptions = indexedOptions
      .filter((o) => this.isBottomPriorityLabel(o.option?.label))
      .map((o) => o.option)
      .sort((a, b) => this.getBottomPriorityOrder(a.label) - this.getBottomPriorityOrder(b.label));
    const topRowOptions = indexedOptions
      .filter((o) => !this.isBottomPriorityLabel(o.option?.label))
      .sort((a, b) => this.compareByFixedPosition(a.option, b.option, a.index, b.index))
      .map((o) => o.option);

    if (bottomPriorityOptions.length > 0 && topRowOptions.length > 0) {
      this.isSpecialSplitLayout = true;
      this.topOptions = topRowOptions;
      this.bottomOptions = bottomPriorityOptions;
      this.displayOptions = [];
      return;
    }

    this.isSpecialSplitLayout = false;
    this.topOptions = topRowOptions;
    this.bottomOptions = [];
    this.displayOptions = this.getDisplayOptions(topRowOptions);
  }

  private isBottomPriorityLabel(label: string): boolean {
    const normalized = String(label || '').trim();
    return normalized === '$' || normalized === '$+%';
  }

  private getBottomPriorityOrder(label: string): number {
    const normalized = String(label || '').trim();
    if (normalized === '$') return 0;
    if (normalized === '$+%') return 1;
    return 99;
  }

  private compareByFixedPosition(
    left: ToggleOption,
    right: ToggleOption,
    leftIndex: number,
    rightIndex: number
  ): number {
    const leftRank = this.getLeftPriorityRank(left?.label);
    const rightRank = this.getLeftPriorityRank(right?.label);

    if (leftRank !== rightRank) {
      return leftRank - rightRank;
    }

    return leftIndex - rightIndex;
  }

  private getLeftPriorityRank(label: string): number {
    const normalized = String(label || '').toLowerCase().trim();
    if (normalized.includes('overview')) return 0;
    if (normalized.includes('quarterly')) return 1;
    if (normalized.includes('by region')) return 2;
    if (normalized.includes('by segment')) return 3;
    if (normalized.includes('by engine model')) return 4;
    return 10;
  }

  private getDisplayOptions(options: ToggleOption[]): ToggleOption[] {
    return options;
  }
}
